// @flow
/* eslint-disable no-underscore-dangle */
import * as querystring from 'query-string';
import type Dayjs from 'dayjs';
import type {
  ApplePaymentData,
  ApplePaymentMethod
} from '../components/Payments/types/types';
import type { ParkingDisruptionParams } from '../types';

const debug = require('debug')('jp:api:gateway');

const { stringify } = querystring;

export default class Api {
  client: any;

  static consents: {
    get: () => Promise<any>,
    deleteAccount: () => Promise<any>,
    create: (Array<any>) => Promise<any>
  };

  static geo: {
    getLocation: any
  };

  constructor(req: any, agent: any, res: any = null) {
    const empty = {};
    const implementation = {
      _locale: null,
      _partner: null,
      _clientPromise: null,
      _clientLoader() {
        if (this._clientPromise !== null) {
          return this._clientPromise;
        }

        debug('Lazy load the legacy API');

        this._clientPromise = import(
          /* webpackChunkName: "legacyApi" */ './apiClientFactory'
        ).then((apiClientFactoryModule) => {
          const factory = apiClientFactoryModule.default;
          const client = factory(req, agent, res);

          if (this._locale) {
            client.setLocale(this._locale);
          }

          if (this._partner) {
            client.setPartner(this._partner);
          }

          if (this._authEmitter) {
            client.setAuthEmitter(this._authEmitter);
          }

          return client;
        });

        return this._clientPromise;
      },
      get(target, prop, receiver) {
        if (prop === 'setLocale') {
          return (value) => {
            this._locale = value;

            if (this._clientPromise) {
              this._clientPromise.then((client) => client.setLocale(value));
            }
          };
        }

        if (prop === 'setPartner') {
          return (value) => {
            this._partner = value;

            if (this._clientPromise) {
              this._clientPromise.then((client) => client.setPartner(value));
            }
          };
        }

        if (prop === 'setAuthEmitter') {
          return (value) => {
            this._authEmitter = value;

            if (this._clientPromise) {
              this._clientPromise.then((client) =>
                client.setAuthEmitter(value)
              );
            }
          };
        }

        if (['get', 'post', 'put', 'patch', 'del'].indexOf(prop) !== -1) {
          return (...args) =>
            this._clientLoader().then((client) => client[prop](...args));
        }

        return Reflect.get(target, prop, receiver);
      }
    };

    this.client = new Proxy(empty, implementation);
  }

  setLocale(locale) {
    debug('set API locale', locale);
    this.client.setLocale(locale);
  }

  setPartner(partner) {
    debug('set API partner', partner);
    this.client.setPartner(partner);
  }

  setAuthEmitter(emitter) {
    this.client.setAuthEmitter(emitter);
  }

  deleteCriteria() {
    debug('delete-criteria');
    return this.client.get('/v5/consents/delete-criteria');
  }

  postcodeLookup(
    country: string,
    postcode: string,
    lastId?: string | null = null
  ) {
    debug('postcode-lookup');
    return this.client.post('/v4/utilities/geo/postcode-lookup', {
      data: {
        postcode,
        country,
        ...(lastId ? { lastId } : null)
      }
    });
  }

  resolveAddress(addressId: string) {
    debug('resolve-address');
    return this.client.post('/v4/utilities/geo/resolve-address', {
      data: {
        addressId
      },
      disableRequestTransform: true
    });
  }

  getDriveUpListings(id: string) {
    debug('getDriveUpListings', id);
    return this.client.get('/v4/listings/driveup', {
      params: {
        'filter[id]': id
      }
    });
  }

  geo = {
    getLocation: (slug: string, locale: string, path?: string): Promise<*> => {
      debug('geo.getLocation', slug, locale);

      return this.client.get(`/v5/geo/${locale}/${slug}`, {
        params: {
          path
        }
      });
    }
  };

  paymentMethods = {
    ddc: (paymentSourceId: number) => {
      debug('paymentMethods.ddc', paymentSourceId);
      return this.client.get(`/v5/3ds/ddc/${paymentSourceId}`);
    },

    jwt: () => this.client.get(`/v5/3ds/ddc`),

    tokenize3DSStep2: (orderCode, sessionId, tokenId, cookie) => {
      debug('paymentMethods.tokenize3DSStep2', {
        orderCode,
        sessionId,
        tokenId,
        cookie
      });
      return this.client.post('/v5/3ds/tokenise/step2', {
        data: {
          order_code: orderCode,
          session_id: sessionId,
          token_id: tokenId,
          cookie
        }
      });
    },

    tokenizeGooglePay: (
      signature: string,
      protocolVersion: string,
      signedMessage: string
    ) => {
      debug('tokenize google pay', signature, protocolVersion, signedMessage);

      return this.client.post('/v5/payments/tokenize/wallet', {
        data: {
          googlepay: {
            signature,
            protocolVersion,
            signedMessage
          }
        }
      });
    },
    tokenizeApplePay: (
      paymentData: ApplePaymentData,
      paymentMethod: ApplePaymentMethod
    ) => {
      debug('tokenize apple pay', paymentData, paymentMethod);

      return this.client.post('/v5/payments/tokenize/wallet', {
        data: {
          applepay: {
            data: {
              data: paymentData.data,
              signature: paymentData.signature,
              version: paymentData.version,
              header: paymentData.header
            },
            payment_network: paymentMethod.network,
            payment_instrument_name: paymentMethod.displayName,
            merchant_id: process.env.APPLE_PAY_MERCHANT_IDENTIFIER
          }
        },
        disableRequestTransform: true
      });
    },
    validateApplePaySession: (validationUrl: string) => {
      debug('validateApplePaySession');

      return this.client.post('/v5/payments/applepay/session', {
        data: {
          validation_url: validationUrl
        }
      });
    },
    createGooglePay: (tokenId: string) => {
      debug('createGooglePaymentMethod');
      return this.client.post('/v4/users/payment-methods', {
        data: {
          tokenId,
          googlepay: tokenId
        }
      });
    },
    createApplePay: (tokenId: string) => {
      debug('createApplePaymentMethod');
      return this.client.post('/v4/users/payment-methods', {
        data: {
          tokenId,
          applepay: tokenId
        }
      });
    }
  };

  phoneNumbers = {
    create: (countryCode: string, phoneNumber: string, delay: number) => {
      debug('phoneNumbers.create', countryCode, phoneNumber, delay);
      return this.client.post('/v4/users/phone', {
        data: {
          countryCode,
          phoneNumber,
          delay
        }
      });
    }
  };

  consents = {
    get: () => {
      debug('consents.get');
      return this.client.get('/v5/consents');
    },
    deleteAccount: () => {
      debug('consents.deleteAccount');
      return this.client.post('/v5/consents', {
        data: {
          data: [
            {
              name: 'standard',
              accepted: false,
              version: 1
            }
          ]
        }
      });
    },
    create: (data: any) => {
      debug('consents.create');
      return this.client.post('/v5/consents', {
        data: {
          data
        }
      });
    }
  };

  listings = {
    add: (data: any, token: string) => {
      debug('listings.add', data);

      return this.client.post('/v4/listings/add', {
        data: {
          data: {
            attributes: data,
            g_recaptcha_response: token
          }
        },
        disableRequestTransform: true
      });
    },

    calculateToEarn: (
      dailyPrice: string,
      monthlyPrice: string,
      duration: number,
      durationType: string,
      listingId: number
    ) => {
      const data = {
        daily_price: parseFloat(dailyPrice) * 100,
        monthly_price: parseFloat(monthlyPrice) * 100,
        duration,
        duration_type: durationType,
        listing_id: listingId
      };
      debug('listings.calculateToEarn', data);

      return this.client.post('v5/pricing/duration-calculator', {
        data,
        disableRequestTransform: true
      });
    },

    edit: (listingId: number, data: any, token: string) => {
      debug('listings.edit', data);

      return this.client.patch(`/v4/listings/${listingId}/edit`, {
        data: {
          data: {
            attributes: data,
            g_recaptcha_response: token
          }
        },
        disableRequestTransform: true
      });
    },

    editSlottedPricing: (listingId: number, data: any) => {
      debug('listings.editSlottedPricing', data);

      return this.client.patch(`/v4/pricing/${listingId}/edit`, {
        data: {
          data: {
            attributes: { ...data, listingId },
            id: listingId
          }
        },
        disableRequestTransform: true
      });
    },

    load: (id: number) => {
      debug('listings.load', id);

      return this.client.get(`/v4/listings/${id}`);
    },

    loadPricing: (id: number) => {
      debug('listings.loadPricing', id);

      return this.client.get(`/v5/pricing/listing/${id}`);
    },

    availability: (listingId: string) => {
      debug('listings.availability', listingId);

      return this.client.get(`/v5/listings/${listingId}/availability`);
    },

    deleteAvailability: (availabilityId: string) => {
      debug('listings.deleteAvailability', availabilityId);

      return this.client.del(`/v5/listings/availability/${availabilityId}`);
    },

    updateAvailabilityDays: (
      listingId: string,
      listingAvailabilityDays: Array<any>
    ) => {
      debug('listings.updateAvailabilityDays', listingId);

      return this.client.patch(`/v5/listings/${listingId}/availability`, {
        params: stringify(
          {
            listing_availability_days: listingAvailabilityDays
          },
          { arrayFormat: 'bracket' }
        ),
        disableRequestTransform: true
      });
    },

    updateAvailability: (
      listingId: string,
      start: Dayjs,
      end: Dayjs,
      numberOfSpaces: number
    ) => {
      debug(
        'listings.updateAvailability',
        listingId,
        start,
        end,
        numberOfSpaces
      );

      return this.client.patch(`/v5/listings/${listingId}/availability`, {
        params: {
          start: start.format(),
          end: end.format(),
          number_of_spaces: numberOfSpaces
        },
        disableRequestTransform: true
      });
    },

    deactivate: (listingId: string, reason: string, details?: string) => {
      debug('listings.deactivate', reason, details);
      return this.client.post(`/v5/listings/${listingId}/deactivate`, {
        data: {
          reason,
          details
        }
      });
    },

    activate: (listingId: string) => {
      debug('listings.activate');
      return this.client.post(`/v5/listings/${listingId}/activate`);
    },

    pendingReasons: (listingId: string) => {
      debug('listings.pendingReasons', listingId);
      return this.client.post(`/v5/listings/pending-reasons`, {
        data: {
          listing_id: listingId
        }
      });
    },

    status: (pageNumber: string = '1', isDeleted: boolean = false) => {
      debug('listings.status');
      return this.client.get(
        `/v5/listings/?page=${pageNumber}&is_deleted=${!isDeleted ? '0' : '1'}`
      );
    },

    pricingSwitch: (listingId: string, pricingType: 'MANUAL' | 'AUTOMATED') => {
      debug('listings.pricingSwitch', listingId, pricingType);

      return this.client.post(`/v5/pricing/switch`, {
        data: {
          listing_id: listingId,
          price_type: pricingType
        }
      });
    },

    getListingParkingDisruptions: ({
      listingId,
      startDate,
      endDate
    }: ParkingDisruptionParams) => {
      debug(`getListingParkingDisruptions`, listingId, startDate, endDate);

      return this.client.get(`/v5/listings/disruption/${listingId}`, {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
    }
  };

  withdrawals = {
    validateBank: (data: any) => {
      debug('withdrawals.validateBank', data);
      return this.client.post('/v4/users/withdrawal-methods/validate-bank', {
        data,
        disableRequestTransform: true
      });
    }
  };
}

export const client = new Api(null, null);

// Using this function instead of the above exported singleton make it easier to mock
export const getClient = () => client;
